import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPinIcon, LocateIcon, PhoneIcon, NavigationIcon, CalendarIcon, LoaderIcon, AlertCircleIcon } from 'lucide-react';
// Location data with coordinates
const locations = [{
  name: 'Clinton',
  phone: '301-877-1770',
  address: '8931 Woodyard Rd',
  cityStateZip: 'Clinton, MD 20735',
  lat: 38.749,
  lng: -76.898
}, {
  name: 'Greenbelt',
  phone: '301-614-3937',
  address: '6088 Greenbelt Road Unit 44',
  cityStateZip: 'Greenbelt, MD 20770',
  lat: 38.9897,
  lng: -76.8781
}, {
  name: 'Hyattsville',
  phone: '301-434-3937',
  address: '1835 University Blvd. Ste 228',
  cityStateZip: 'Hyattsville, MD 20783',
  lat: 38.9554,
  lng: -76.9569
}, {
  name: 'Mitchellville',
  phone: '301-324-9500',
  address: '10240 Lake Arbor Way',
  cityStateZip: 'Mitchellville, MD 20721',
  lat: 38.926,
  lng: -76.834,
  isMain: true
}, {
  name: 'Silver Spring',
  phone: '301-681-9797',
  address: '8121 Georgia Ave, Ste 800',
  cityStateZip: 'Silver Spring, MD 20910',
  lat: 38.996,
  lng: -77.028
}, {
  name: 'Frederick',
  phone: '301-694-7557',
  address: '61 Thomas Johnson Dr, Ste 201',
  cityStateZip: 'Frederick, MD 21702',
  lat: 39.3943,
  lng: -77.4106
}];
const BOOKING_URL = 'https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0';
// Haversine formula to calculate distance in miles
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 3958.8; // Radius of the earth in miles
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in miles
  return d;
}
function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}
type LocationWithDistance = (typeof locations)[0] & {
  distance: number;
};
export function NearestLocationFinder() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'denied'>('idle');
  const [sortedLocations, setSortedLocations] = useState<LocationWithDistance[]>([]);
  const [userPosition, setUserPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const findNearest = () => {
    if (!navigator.geolocation) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    navigator.geolocation.getCurrentPosition(position => {
      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;
      setUserPosition({
        lat: userLat,
        lng: userLng
      });
      const locationsWithDistance = locations.map(loc => ({
        ...loc,
        distance: calculateDistance(userLat, userLng, loc.lat, loc.lng)
      }));
      locationsWithDistance.sort((a, b) => a.distance - b.distance);
      setSortedLocations(locationsWithDistance);
      setStatus('success');
    }, error => {
      if (error.code === error.PERMISSION_DENIED) {
        setStatus('denied');
      } else {
        setStatus('error');
      }
    }, {
      timeout: 10000
    });
  };
  return <section className="py-16 md:py-20 bg-white border-y border-surface-muted" data-id="element-380">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-381">
        <div className="max-w-4xl mx-auto text-center" data-id="element-382">
          <AnimatePresence mode="wait" data-id="element-383">
            {/* IDLE & LOADING STATE */}
            {(status === 'idle' || status === 'loading') && <motion.div key="idle" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="flex flex-col items-center" data-id="element-384">
                <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center mb-6" data-id="element-385">
                  <MapPinIcon className="w-8 h-8 text-brand-gold" data-id="element-386" />
                </div>
                <h2 className="text-section-mobile md:text-section text-brand-dark font-heading font-bold mb-4" data-id="element-387">
                  Find Your Nearest Eyecare Center
                </h2>
                <p className="text-neutral-text/70 mb-8 max-w-lg mx-auto" data-id="element-388">
                  Use your location to find the VisualEyes clinic closest to
                  you.
                </p>

                <button onClick={findNearest} disabled={status === 'loading'} className="flex items-center gap-2 bg-brand-gold text-white px-8 py-4 rounded-button font-heading font-semibold hover:bg-brand-gold-light transition-colors shadow-md focus-ring disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-389">
                  {status === 'loading' ? <>
                      <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-390" />
                      Finding your location...
                    </> : <>
                      <LocateIcon className="w-5 h-5" data-id="element-391" />
                      Find Nearest Eyecare Center
                    </>}
                </button>
                <p className="text-xs text-neutral-text/50 mt-4" data-id="element-392">
                  We only use your location to find the nearest clinic.
                </p>
              </motion.div>}

            {/* SUCCESS STATE */}
            {status === 'success' && sortedLocations.length > 0 && <motion.div key="success" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="w-full" data-id="element-393">
                <div className="mb-10" data-id="element-394">
                  <h2 className="text-3xl font-heading font-bold text-brand-dark mb-2" data-id="element-395">
                    Closest Locations
                  </h2>
                  <p className="text-neutral-text/70" data-id="element-396">
                    Based on your current location
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-4 mb-10 text-left" data-id="element-397">
                  {sortedLocations.slice(0, 3).map((loc, index) => {
                const isNearest = index === 0;
                return <motion.div key={loc.name} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: index * 0.1
                }} className={`bg-surface-muted rounded-card p-6 relative flex flex-col h-full ${isNearest ? 'border-2 border-brand-gold shadow-md bg-white' : 'border border-gray-100'}`} data-id="element-398">
                        {isNearest && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-gold text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap shadow-sm" data-id="element-399">
                            Nearest to You
                          </div>}

                        <div className="mb-4 pt-2" data-id="element-400">
                          <h3 className="text-xl font-heading font-bold text-brand-dark flex items-center gap-2" data-id="element-401">
                            {loc.name}
                            {loc.isMain && <span className="text-[10px] bg-brand-dark text-white px-2 py-0.5 rounded-sm uppercase tracking-wider font-normal" data-id="element-402">
                                Main
                              </span>}
                          </h3>
                          <p className="text-brand-gold font-semibold mt-1" data-id="element-403">
                            {loc.distance.toFixed(1)} miles away
                          </p>
                        </div>

                        <div className="space-y-3 mb-6 flex-grow" data-id="element-404">
                          <p className="text-sm text-neutral-text/70" data-id="element-405">
                            {loc.address}
                            <br data-id="element-406" />
                            {loc.cityStateZip}
                          </p>
                          <a href={`tel:${loc.phone.replace(/-/g, '')}`} className="flex items-center gap-2 text-sm text-brand-dark font-medium hover:text-brand-gold transition-colors w-fit" data-id="element-407">
                            <PhoneIcon className="w-4 h-4 text-brand-gold" data-id="element-408" />
                            {loc.phone}
                          </a>
                        </div>

                        <div className="space-y-3 mt-auto" data-id="element-409">
                          <a href={`https://www.google.com/maps/dir/?api=1&origin=${userPosition?.lat},${userPosition?.lng}&destination=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full bg-white border border-gray-200 text-brand-dark py-2.5 rounded-button text-sm font-semibold hover:border-brand-gold hover:text-brand-gold transition-colors focus-ring" data-id="element-410">
                            <NavigationIcon className="w-4 h-4" data-id="element-411" />
                            Get Directions
                          </a>
                          <a href={BOOKING_URL} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full bg-brand-dark text-white py-2.5 rounded-button text-sm font-semibold hover:bg-brand-gold transition-colors focus-ring" data-id="element-412">
                            <CalendarIcon className="w-4 h-4" data-id="element-413" />
                            Book Appointment
                          </a>
                        </div>
                      </motion.div>;
              })}
                </div>

                <Link to="/locations" className="inline-flex items-center text-brand-dark font-semibold hover:text-brand-gold transition-colors focus-ring rounded-sm px-2 py-1" data-id="element-414">
                  View All Locations &rarr;
                </Link>
              </motion.div>}

            {/* ERROR / DENIED STATE */}
            {(status === 'error' || status === 'denied') && <motion.div key="error" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="bg-red-50 border border-red-100 rounded-card p-8 max-w-lg mx-auto" data-id="element-415">
                <AlertCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" data-id="element-416" />
                <h3 className="text-xl font-heading font-bold text-brand-dark mb-2" data-id="element-417">
                  {status === 'denied' ? 'Location Access Denied' : 'Location Error'}
                </h3>
                <p className="text-neutral-text/70 mb-6" data-id="element-418">
                  {status === 'denied' ? 'Location access was denied. You can still browse all our locations.' : 'Unable to determine your location. Please check your browser settings or try again.'}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-419">
                  {status === 'error' && <button onClick={findNearest} className="bg-white border border-gray-200 text-brand-dark px-6 py-2.5 rounded-button font-semibold hover:bg-gray-50 transition-colors focus-ring" data-id="element-420">
                      Try Again
                    </button>}
                  <Link to="/locations" className="bg-brand-gold text-white px-6 py-2.5 rounded-button font-semibold hover:bg-brand-gold-light transition-colors focus-ring" data-id="element-421">
                    Browse All Locations
                  </Link>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}